import { useState, useEffect, useRef } from "react";

const C = {
  porcelain: "#F5F2EC",
  parchment: "#EDE7DA",
  bone:      "#D6CFC2",
  stone:     "#B8AA98",
  slate:     "#2A2925",
  warm:      "#7A6F62",
  brass:     "#A8894A",
  brassLight:"#C9A96E",
  white:     "#FDFCF9",
};

const VALID_CODES = ["PEMBERTON", "PEMBERTON2025", "MERCER", "MEDINA", "DEMO"];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(18px)",
      transition: `opacity 0.9s ease ${delay}s, transform 0.9s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

export default function PembertonSite() {
  const [code, setCode]         = useState("");
  const [status, setStatus]     = useState("idle"); // idle | success | error
  const [focused, setFocused]   = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) { setStatus("error"); return; }
    if (VALID_CODES.includes(trimmed)) {
      setStatus("success");
    } else {
      setStatus("error");
    }
  };

  const services = [
    {
      name: "Standard Cleaning",
      freq: "Biweekly recurring",
      desc: "A thorough maintenance clean of your entire home. The same lead cleaner, every visit. Confirmed within a 30-minute arrival window. Nothing is required from you.",
      detail: ["Full home, top to bottom", "Same lead cleaner each visit", "30-minute arrival window", "Proactive status notifications"],
    },
    {
      name: "Deep Cleaning",
      freq: "First clean · Seasonal",
      desc: "Every new client begins here. A comprehensive baseline clean that reaches what standard maintenance does not — interiors of appliances, baseboards, blind slats, cabinet faces.",
      detail: ["All standard areas, plus", "Interior appliances", "Baseboards · blind slats", "Cabinet faces · light fixtures"],
    },
    {
      name: "Move-In Preparation",
      freq: "One-time",
      desc: "Your new home, ready before you arrive. We treat an empty property thoroughly — every surface, every room — so your first night feels exactly as it should.",
      detail: ["Full empty-home detail", "Interior cabinets and closets", "All surfaces, fixtures, floors", "Completed before move-in day"],
    },
  ];

  return (
    <div style={{ background: C.porcelain, color: C.slate, fontFamily: "'Jost', sans-serif", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: #F5F2EC; }

        ::selection { background: #2A2925; color: #F5F2EC; }

        .nav-link {
          font-size: 9px; letter-spacing: 0.22em; font-weight: 500;
          color: #B8AA98; text-decoration: none; font-family: 'Jost', sans-serif;
          transition: color 0.2s;
          cursor: pointer;
          background: none; border: none;
        }
        .nav-link:hover { color: #2A2925; }

        .referral-input {
          background: none;
          border: none;
          border-bottom: 1px solid #D6CFC2;
          outline: none;
          font-family: 'Jost', sans-serif;
          font-size: 13px;
          font-weight: 300;
          letter-spacing: 0.18em;
          color: #2A2925;
          padding: 14px 0;
          width: 100%;
          text-transform: uppercase;
          transition: border-color 0.3s;
        }
        .referral-input::placeholder { color: #D6CFC2; letter-spacing: 0.18em; }
        .referral-input:focus { border-bottom-color: #2A2925; }

        .submit-btn {
          background: #2A2925;
          border: none;
          color: #F5F2EC;
          font-family: 'Jost', sans-serif;
          font-size: 9px;
          font-weight: 500;
          letter-spacing: 0.22em;
          padding: 16px 36px;
          cursor: pointer;
          transition: background 0.2s, transform 0.1s;
        }
        .submit-btn:hover { background: #A8894A; }
        .submit-btn:active { transform: scale(0.98); }

        .service-card {
          padding: 40px 0;
          border-top: 1px solid #D6CFC2;
          transition: background 0.2s;
        }

        .hairline { width: 100%; height: 1px; background: #D6CFC2; }
        .brass-rule { width: 80px; height: 0.5px; background: #A8894A; }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .page-enter { animation: fadeIn 1.2s ease forwards; }

        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        .cursor { display: inline-block; width: 1px; height: 1em;
          background: #A8894A; margin-left: 2px;
          animation: blink 1.2s ease infinite; vertical-align: baseline; }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: "0 64px",
        height: 64,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: scrolled ? "rgba(245,242,236,0.96)" : "transparent",
        borderBottom: scrolled ? `1px solid ${C.bone}` : "1px solid transparent",
        backdropFilter: scrolled ? "blur(8px)" : "none",
        transition: "all 0.4s ease",
      }}>
        <div style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 16, fontWeight: 300, letterSpacing: "0.12em",
          color: C.slate,
        }}>
          Pemberton Co.
        </div>
        <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
          <button className="nav-link" onClick={() => document.getElementById("services")?.scrollIntoView({ behavior:"smooth" })}>
            SERVICES
          </button>
          <button className="nav-link" onClick={() => document.getElementById("referral")?.scrollIntoView({ behavior:"smooth" })}>
            REQUEST ACCESS
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ padding: "180px 64px 140px", maxWidth: 1100, margin: "0 auto" }} className="page-enter">
        <div style={{ maxWidth: 680 }}>

          {/* Eyebrow */}
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 56 }}>
            <div className="brass-rule"/>
            <div style={{ fontSize: 9, letterSpacing: "0.28em", color: C.stone, fontWeight: 500 }}>
              PEMBERTON CO. · PREMIUM RESIDENTIAL SERVICES
            </div>
          </div>

          {/* Main headline */}
          <h1 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "clamp(52px, 6vw, 82px)",
            fontWeight: 300,
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
            color: C.slate,
            marginBottom: 48,
          }}>
            Precision<br/>
            <em style={{ fontStyle: "italic", color: C.warm }}>and discretion.</em>
          </h1>

          {/* Sub */}
          <p style={{
            fontSize: 15, fontWeight: 300, lineHeight: 1.95,
            color: C.warm, maxWidth: 440, marginBottom: 56,
            letterSpacing: "0.02em",
          }}>
            We maintain exceptional homes in Mercer Island, Medina, Hunts Point, and the greater Eastside — quietly, reliably, and without requiring anything from you.
          </p>

          {/* Service area pills */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 64 }}>
            {["Mercer Island", "Medina", "Hunts Point", "Clyde Hill", "Madison Park", "Broadmoor"].map(area => (
              <div key={area} style={{
                padding: "6px 14px",
                border: `1px solid ${C.bone}`,
                fontSize: 9, letterSpacing: "0.16em", fontWeight: 500,
                color: C.stone,
              }}>
                {area.toUpperCase()}
              </div>
            ))}
          </div>

          {/* CTA nudge */}
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <button className="submit-btn" onClick={() => document.getElementById("referral")?.scrollIntoView({ behavior: "smooth" })}>
              REQUEST ACCESS
            </button>
            <div style={{ fontSize: 10, color: C.stone, letterSpacing: "0.1em" }}>
              BY REFERRAL ONLY
            </div>
          </div>
        </div>
      </section>

      {/* ── DIVIDER STATEMENT ── */}
      <Reveal>
        <div style={{
          borderTop: `1px solid ${C.bone}`, borderBottom: `1px solid ${C.bone}`,
          padding: "56px 64px",
          display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
          gap: 0,
          maxWidth: 1100, margin: "0 auto 0",
        }}>
          {[
            { label: "ARRIVAL WINDOW", value: "30 minutes", sub: "Not a 2-hour window. Thirty minutes." },
            { label: "COMMUNICATION", value: "Proactive", sub: "You're notified before you think to ask." },
            { label: "FOLLOW-UP REQUIRED", value: "None.", sub: "The work is done. So is our job." },
          ].map((s, i) => (
            <div key={i} style={{
              padding: "0 40px",
              borderLeft: i > 0 ? `1px solid ${C.bone}` : "none",
            }}>
              <div style={{ fontSize: 9, letterSpacing: "0.2em", color: C.stone, fontWeight: 500, marginBottom: 14 }}>
                {s.label}
              </div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 28, fontWeight: 300, color: C.slate,
                letterSpacing: "0.04em", marginBottom: 10,
              }}>
                {s.value}
              </div>
              <div style={{ fontSize: 11, color: C.stone, fontWeight: 300, lineHeight: 1.7 }}>
                {s.sub}
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* ── SERVICES ── */}
      <section id="services" style={{ padding: "120px 64px", maxWidth: 1100, margin: "0 auto" }}>
        <Reveal>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 72 }}>
            <div>
              <div style={{ fontSize: 9, letterSpacing: "0.24em", color: C.stone, fontWeight: 500, marginBottom: 16 }}>
                INTERIOR CLEANING
              </div>
              <h2 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 300,
                color: C.slate, letterSpacing: "0.02em", lineHeight: 1.2,
              }}>
                What we do,<br/>and how we do it.
              </h2>
            </div>
            <div style={{ maxWidth: 300, textAlign: "right" }}>
              <p style={{ fontSize: 12, color: C.stone, fontWeight: 300, lineHeight: 1.9 }}>
                Every service begins with a deep clean to establish the standard your home deserves. Recurring visits maintain it — quietly, consistently, without your involvement.
              </p>
            </div>
          </div>
        </Reveal>

        {services.map((svc, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div className="service-card">
              <div style={{
                display: "grid",
                gridTemplateColumns: "280px 1fr 220px",
                gap: 48, alignItems: "start",
              }}>
                {/* Name + freq */}
                <div>
                  <div style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 26, fontWeight: 300,
                    color: C.slate, letterSpacing: "0.04em",
                    marginBottom: 10,
                  }}>
                    {svc.name}
                  </div>
                  <div style={{ fontSize: 9, letterSpacing: "0.18em", color: C.brass, fontWeight: 500 }}>
                    {svc.freq.toUpperCase()}
                  </div>
                </div>

                {/* Description */}
                <p style={{
                  fontSize: 14, fontWeight: 300, lineHeight: 1.9,
                  color: C.warm, letterSpacing: "0.02em",
                }}>
                  {svc.desc}
                </p>

                {/* Detail list */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {svc.detail.map((d, j) => (
                    <div key={j} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                      <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.bone, flexShrink: 0, marginTop: 6 }}/>
                      <div style={{ fontSize: 11, color: C.stone, fontWeight: 300, lineHeight: 1.6 }}>{d}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* ── HOW IT WORKS ── */}
      <Reveal>
        <div style={{
          background: C.parchment,
          borderTop: `1px solid ${C.bone}`, borderBottom: `1px solid ${C.bone}`,
          padding: "100px 64px",
        }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ fontSize: 9, letterSpacing: "0.24em", color: C.stone, fontWeight: 500, marginBottom: 64 }}>
              HOW IT WORKS
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0 }}>
              {[
                { n: "01", title: "You're referred", body: "A current Pemberton client shares their referral code with you. This is the only way in." },
                { n: "02", title: "We speak once", body: "A brief onboarding call — your home, your preferences, your schedule. We won't need to ask again." },
                { n: "03", title: "First visit", body: "A deep clean establishes the standard. From here, your home is maintained on autopilot." },
                { n: "04", title: "Nothing required", body: "Confirmations, notifications, invoices — handled. You come home to a clean house. That's all." },
              ].map((step, i) => (
                <div key={i} style={{
                  padding: "0 40px",
                  borderLeft: i > 0 ? `1px solid ${C.bone}` : "none",
                }}>
                  <div style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 48, fontWeight: 300, color: C.bone,
                    lineHeight: 1, marginBottom: 24, letterSpacing: "0.04em",
                  }}>
                    {step.n}
                  </div>
                  <div style={{
                    fontSize: 15, fontWeight: 400, color: C.slate,
                    marginBottom: 16, letterSpacing: "0.02em",
                    fontFamily: "'Cormorant Garamond', serif",
                  }}>
                    {step.title}
                  </div>
                  <p style={{ fontSize: 12, color: C.stone, fontWeight: 300, lineHeight: 1.8 }}>
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── REFERRAL GATE ── */}
      <section id="referral" style={{ padding: "140px 64px", maxWidth: 1100, margin: "0 auto" }}>
        <Reveal>
          <div style={{ maxWidth: 560 }}>
            <div style={{ fontSize: 9, letterSpacing: "0.24em", color: C.stone, fontWeight: 500, marginBottom: 48 }}>
              REQUEST ACCESS
            </div>

            <h2 style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(36px, 4vw, 52px)", fontWeight: 300,
              color: C.slate, letterSpacing: "0.02em", lineHeight: 1.15,
              marginBottom: 28,
            }}>
              Pemberton accepts new clients by referral only.
            </h2>

            <p style={{
              fontSize: 14, fontWeight: 300, lineHeight: 1.9,
              color: C.warm, marginBottom: 56,
            }}>
              If someone you trust has shared a referral code with you, enter it below. We'll reach out within 24 hours to schedule your onboarding call.
            </p>

            {status !== "success" ? (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{
                    fontSize: 9, letterSpacing: "0.2em", color: C.stone,
                    fontWeight: 500, marginBottom: 16,
                  }}>
                    REFERRAL CODE
                  </div>
                  <input
                    className="referral-input"
                    type="text"
                    placeholder="ENTER CODE"
                    value={code}
                    onChange={e => { setCode(e.target.value); setStatus("idle"); }}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {status === "error" && (
                    <div style={{
                      fontSize: 10, color: "#8B4040", letterSpacing: "0.12em",
                      fontWeight: 500, marginTop: 10,
                    }}>
                      {code.trim() ? "CODE NOT RECOGNIZED. PLEASE CHECK WITH YOUR REFERRAL SOURCE." : "PLEASE ENTER YOUR REFERRAL CODE."}
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 24, marginTop: 32 }}>
                  <button className="submit-btn" type="submit">
                    SUBMIT
                  </button>
                  <div style={{ fontSize: 10, color: C.bone, letterSpacing: "0.12em" }}>
                    DO NOT HAVE A CODE?
                  </div>
                </div>
              </form>
            ) : (
              <div style={{
                borderLeft: `0.5px solid ${C.brass}`,
                paddingLeft: 28,
              }}>
                <div style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 26, fontWeight: 300,
                  color: C.slate, letterSpacing: "0.04em",
                  marginBottom: 16, lineHeight: 1.3,
                }}>
                  Code accepted.
                </div>
                <p style={{ fontSize: 13, color: C.warm, fontWeight: 300, lineHeight: 1.9, marginBottom: 28 }}>
                  Thank you. We'll be in touch within 24 hours to schedule your onboarding call. Please check the email associated with your referral.
                </p>
                <div style={{ fontSize: 9, letterSpacing: "0.18em", color: C.stone, fontWeight: 500 }}>
                  PEMBERTON CO. · MERCER ISLAND · MEDINA · EASTSIDE
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: C.slate, padding: "64px 64px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 48 }}>
            <div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 24, fontWeight: 300, letterSpacing: "0.1em",
                color: C.porcelain, marginBottom: 8,
              }}>
                Pemberton Co.
              </div>
              <div style={{ width: 60, height: 0.5, background: C.brassLight, marginBottom: 12 }}/>
              <div style={{ fontSize: 9, letterSpacing: "0.22em", color: C.stone, fontWeight: 500 }}>
                PREMIUM RESIDENTIAL SERVICES
              </div>
            </div>

            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 9, letterSpacing: "0.18em", color: C.stone, fontWeight: 500, marginBottom: 12 }}>
                SERVICE AREA
              </div>
              <div style={{ fontSize: 12, color: "rgba(245,242,236,0.5)", fontWeight: 300, lineHeight: 2 }}>
                Mercer Island · Medina · Hunts Point<br/>
                Clyde Hill · Madison Park · Broadmoor
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: "rgba(255,255,255,0.06)", marginBottom: 32 }}/>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.2)", fontWeight: 300, letterSpacing: "0.08em" }}>
              © 2025 Pemberton Co. · Seattle, Washington · By referral only.
            </div>
            <div style={{ fontSize: 9, color: C.stone, letterSpacing: "0.16em", fontWeight: 500 }}>
              pembertonco.com
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
